import { CommonModule } from '@angular/common';
import { Component, Output, EventEmitter, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormsService } from '../../../pages/forms/forms.service';
import { take } from 'rxjs';
interface BodyPoint {
  key: string;
  label: string;
  top: number;      // 點的位置（百分比）
  left: number;     // 點的位置（百分比）
  labelTop?: number;
  labelLeft?: number;
  questionId?: number
}
@Component({
  selector: 'app-body-pain-selector',
  imports: [CommonModule, FormsModule],
  templateUrl: './body-pain-selector.component.html',
  styleUrl: './body-pain-selector.component.scss'
})

export class BodyPainSelectorComponent implements OnInit {
  // 🔥 獲取 HTML 中的 slider 元素
  @ViewChild('painSlider') painSlider!: ElementRef<HTMLInputElement>;
  constructor(private formsService: FormsService) {

  }
  ngOnInit(): void {
    // 🔥 1. 初始化所有部位的預設值為 0
    this.bodyPoints.forEach(point => {
      this.painMap[point.key] = 0;
    });
    //  【核心：從 Service 恢復記憶】
    // 使用 getValue() 直接同步取得 Service 當前儲存的資料，這比 subscribe 更安全
    const savedData = this.formsService.getPainDataValue(); // 假設你在 Service 加
    if (savedData && savedData.length > 0) {
      savedData.forEach(item => {
        // 透過當初存入的 key (例如 'neck') 找回對應的疼痛分數
        if (item.key && this.painMap.hasOwnProperty(item.key)) {
          this.painMap[item.key] = Number(item.answer);
        }
      });
    }
    // 訂閱 Service，把之前存過的資料倒填回 painMap
    // 使用 take(1) 確保只在初始化時抓一次，避免無窮迴圈
    this.formsService.painData$.pipe(take(1)).subscribe(savedData => {
      if (savedData && savedData.length > 0) {
        savedData.forEach(item => {
          // 我們根據 key 來對應回 painMap
          if (item.key && this.painMap.hasOwnProperty(item.key)) {
            this.painMap[item.key] = Number(item.answer);
          }
        });
        // 恢復資料後，記得更新 Slider 顏色
        this.updateSliderStyle();
      }
    });
    // 3. 初始完發送一次完整狀態
    this.formsService.updatePainData(this.selectedPainList);
  }
  bodyPoints: BodyPoint[] = [
    { key: 'neck', label: '頸', top: 18, left: 50, labelTop: 16, labelLeft: 18, questionId: 31 },
    { key: 'leftShoulder', label: '左肩', top: 25, left: 36, labelTop: 26, labelLeft: 10, questionId: 32 },
    { key: 'leftForearm', label: '左手肘/左前臂', top: 40, left: 34, labelTop: 40, labelLeft: 8, questionId: 33 },
    { key: 'leftWristHand', label: '左手/左手腕', top: 55, left: 30, labelTop: 54, labelLeft: 10, questionId: 34 },
    { key: 'leftHipThigh', label: '左臀/左大腿', top: 68, left: 42, labelTop: 68, labelLeft: 15, questionId: 35 },
    { key: 'leftKnee', label: '左膝', top: 81, left: 40, labelTop: 82, labelLeft: 18, questionId: 36 },
    { key: 'leftAnkleFoot', label: '左腳踝/左腳', top: 93, left: 40, labelTop: 94, labelLeft: 14, questionId: 37 },
    { key: 'upperBack', label: '上背', top: 30, left: 50, labelTop: 14, labelLeft: 82, questionId: 38 },
    { key: 'rightShoulder', label: '右肩', top: 25, left: 64, labelTop: 26, labelLeft: 84, questionId: 39 },
    { key: 'rightForearm', label: '右手肘/右前臂', top: 40, left: 66, labelTop: 40, labelLeft: 87, questionId: 40 },
    { key: 'lowerBack', label: '下背', top: 45, left: 50, labelTop: 50, labelLeft: 83, questionId: 41 },
    { key: 'rightWristHand', label: '右手/右手腕', top: 55, left: 71, labelTop: 56, labelLeft: 88, questionId: 42 },
    { key: 'rightHipThigh', label: '右臀/右大腿', top: 68, left: 58, labelTop: 68, labelLeft: 84, questionId: 43 },
    { key: 'rightKnee', label: '右膝', top: 81, left: 60, labelTop: 82, labelLeft: 82, questionId: 44 },
    { key: 'rightAnkleFoot', label: '右腳踝/右腳', top: 93, left: 62, labelTop: 94, labelLeft: 84, questionId: 45 }
  ];
  @Output() painDataChange = new EventEmitter<any[]>();
  selectedIndex = 0;

  painMap: Record<string, number> = {};
  get currentPoint(): BodyPoint | null {
    return this.bodyPoints[this.selectedIndex] ?? null;
  }
  // 🔥 這是專門給 HTML 顯示用的，只回傳有分數的
  get filteredDisplayList() {
    return this.selectedPainList.filter(item => item.pain > 0);
  }
  // 判斷是否有任何一個部位的分數大於 0
  get hasPainData(): boolean {
    return this.selectedPainList.some(item => item.pain > 0);
  }
  get selectedPainList() {
    const START_ID = 91; // neck 0 分的起始 ID
    return this.bodyPoints.map((point, index) => {
      const painLevel = this.painMap[point.key] ?? 0;
      const autoChoiceId = String((index * 6) + START_ID + painLevel);
      return {
        key: point.key,
        label: point.label,
        pain: painLevel,
        choiceId: autoChoiceId,
        questionId: point.questionId,
        answer: String(painLevel)
      };
    });
  }

  selectPoint(index: number): void {
    this.selectedIndex = index;
    this.updateSliderStyle();
  }

  prevPoint(): void {
    if (this.bodyPoints.length === 0) return;
    this.selectedIndex =
      this.selectedIndex === 0
        ? this.bodyPoints.length - 1
        : this.selectedIndex - 1;
    this.updateSliderStyle();
  }

  nextPoint(): void {
    if (this.bodyPoints.length === 0) return;

    this.selectedIndex =
      this.selectedIndex === this.bodyPoints.length - 1
        ? 0
        : this.selectedIndex + 1;
    this.updateSliderStyle();
  }

  updatePainLevel(value: number | string): void {
    if (!this.currentPoint) return;
    this.painMap[this.currentPoint.key] = Number(value);
    // 直接透過 Service 廣播出去，不需使用 @Output
    this.formsService.updatePainData(this.selectedPainList);
    //提醒父層已經選過資料了
    this.formsService.hasOrNotPain(true)
    // 🔥 呼叫更新樣式方法
    this.updateSliderStyle();
  }
  // 🔥 核心：計算百分比並套用漸層背景
  private updateSliderStyle(): void {
    // 使用 setTimeout 確保 Angular 已經將新數值渲染到 input 的 value 上
    setTimeout(() => {
      if (!this.painSlider) return;

      const slider = this.painSlider.nativeElement;
      // 從 painMap 抓取目前選中部位的分數，若無則為 0
      const currentKey = this.bodyPoints[this.selectedIndex]?.key;
      const value = this.painMap[currentKey] ?? 0;

      const max = Number(slider.max) || 5;
      const min = Number(slider.min) || 0;
      const percent = ((value - min) / (max - min)) * 100;

      // 更新背景：左側綠色，右側灰色
      slider.style.background = `linear-gradient(to right, #1fa7a4 ${percent}%, #eef1f4 ${percent}%)`;
    });
  }
  getPointStyle(point: BodyPoint) {
    return {
      top: `${point.top}%`,
      left: `${point.left}%`
    };
  }

  getLabelStyle(point: BodyPoint) {
    return {
      top: `${point.labelTop ?? point.top}%`,
      left: `${point.labelLeft ?? point.left}%`
    };
  }

  isActivePoint(index: number): boolean {
    return this.selectedIndex === index;
  }

  hasPain(point: BodyPoint): boolean {
    return (this.painMap[point.key] ?? 0) > 0;
  }
}
